import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const FROM = "2026-10-02";
const TO = "2026-11-02";
const card = "rounded-xl border border-slate-200 bg-white p-4";

function Row({ r, picked, toggle, preview }) {
  const ok = r.class === "ELIGIBLE";
  return (
    <tr className="border-t border-slate-100 text-[12px]" data-testid={`lfb-row-${r.code}`}>
      <td className="p-2"><input type="checkbox" disabled={!ok} checked={picked} onChange={() => toggle(r.code)} data-testid={`lfb-pick-${r.code}`} /></td>
      <td className="p-2 font-semibold">{r.code}</td>
      <td className="p-2">{r.recipient || "—"}</td>
      <td className="p-2 font-mono">{r.email || "—"}</td>
      <td className="p-2">{r.dispatch_ist || "—"}</td>
      <td className={`p-2 font-bold ${ok ? "text-emerald-700" : "text-slate-500"}`} data-testid={`lfb-class-${r.code}`}>{r.class}{r.reason ? ` · ${r.reason}` : ""}</td>
      <td className="p-2">{r.email && <button className="underline" onClick={() => preview(r.code)} data-testid={`lfb-preview-${r.code}`}>Preview</button>}</td>
    </tr>
  );
}

function JobPanel() {
  const [dr, setDr] = useState(null);
  const [confirm, setConfirm] = useState("");
  const load = () => api.get("/smart-fulfilment/feedback-job/dry-run").then((r) => setDr(r.data)).catch(() => toast.error("Could not load the dry run"));
  useEffect(() => { load(); }, []);
  const flip = (enabled) => api.post("/smart-fulfilment/feedback-job/switch", { enabled, confirm }).then((r) => { setDr(r.data); setConfirm(""); toast.success(enabled ? "Feedback job ON" : "Feedback job OFF"); })
    .catch((e) => toast.error(e?.response?.data?.detail || "Switch failed"));
  if (!dr) return null;
  const list = (rows) => (rows.length ? rows.map((x) => <div key={x.code}>{x.code} · {x.recipient || ""} · {x.email} · {x.dispatch || x.dispatch_ist || ""}</div>) : <div>none</div>);
  return (
    <div className={card} data-testid="lfb-job-panel">
      <div className="mb-1 text-sm font-bold">Automatic delivery feedback (feedback only — general reminders stay off)</div>
      <div className="text-[12px] text-slate-600">Status: <b data-testid="lfb-job-status">{dr.config.enabled ? "ON" : "OFF"}</b> · cutoff STX {dr.config.floor} · SHP {dr.config.legacy_floor} · one request 7 days after dispatch, nothing before the cutoff is ever replayed.</div>
      <div className="mt-2 grid gap-2 text-[11px] md:grid-cols-3" data-testid="lfb-job-dryrun">
        <div><b>Next tick would request (STX)</b>{list(dr.would_request)}</div>
        <div><b>Next tick would remind (STX)</b>{list(dr.would_remind)}</div>
        <div><b>Next tick would request (SHP)</b>{list(dr.legacy_would_request)}</div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type ENABLE FEEDBACK JOB" className="rounded-md border border-slate-300 px-2 py-1 text-[12px]" data-testid="lfb-job-confirm" />
        <button onClick={() => flip(true)} disabled={dr.config.enabled || confirm !== "ENABLE FEEDBACK JOB"} className="rounded-full bg-emerald-700 px-4 py-1.5 text-[12px] font-bold text-white disabled:bg-slate-300" data-testid="lfb-job-enable">Enable feedback job</button>
        <button onClick={() => flip(false)} disabled={!dr.config.enabled} className="rounded-full border border-slate-300 px-4 py-1.5 text-[12px] font-bold disabled:opacity-40" data-testid="lfb-job-disable">Turn off</button>
        <button onClick={load} className="text-[12px] underline" data-testid="lfb-job-refresh">Refresh dry run</button>
      </div>
    </div>
  );
}

function CustomerNoticePanel() {
  const [sw, setSw] = useState(null);
  const [confirm, setConfirm] = useState("");
  useEffect(() => { api.get("/customer-notices/switch").then((r) => setSw(r.data)).catch(() => setSw({})); }, []);
  const flip = (enabled) => api.post("/customer-notices/switch", { enabled, confirm }).then((r) => { setSw(r.data); setConfirm(""); }).catch((e) => toast.error(e?.response?.data?.detail || "Switch failed"));
  if (!sw) return null;
  return (
    <div className={card} data-testid="cn-switch-panel">
      <div className="text-sm font-bold">Customer order dispatch updates (batch email to customer logistics contacts)</div>
      <div className="text-[12px] text-slate-600">Status: <b data-testid="cn-switch-status">{sw.enabled ? "ON" : "OFF"}</b>{sw.at ? ` · since ${sw.at.slice(0, 16)} by ${sw.by}` : ""} · only dispatches after switch-on are included; no earlier shipment is ever replayed.</div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type ENABLE CUSTOMER NOTICES" className="rounded-md border border-slate-300 px-2 py-1 text-[12px]" data-testid="cn-switch-confirm" />
        <button onClick={() => flip(true)} disabled={sw.enabled || confirm !== "ENABLE CUSTOMER NOTICES"} className="rounded-full bg-emerald-700 px-4 py-1.5 text-[12px] font-bold text-white disabled:bg-slate-300" data-testid="cn-switch-enable">Enable customer updates</button>
        <button onClick={() => flip(false)} disabled={!sw.enabled} className="rounded-full border border-slate-300 px-4 py-1.5 text-[12px] font-bold disabled:opacity-40" data-testid="cn-switch-disable">Turn off</button>
      </div>
    </div>
  );
}

export default function LegacyFeedbackCatchup() {
  const [audit, setAudit] = useState(null);
  const [picked, setPicked] = useState([]);
  const [confirm, setConfirm] = useState("");
  const [html, setHtml] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/smart-fulfilment/legacy-feedback/audit", { params: { date_from: FROM, date_to: TO } }).then((r) => setAudit(r.data)).catch(() => toast.error("Could not load the audit"));
  useEffect(() => { load(); }, []);

  const toggle = (c) => setPicked((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
  const preview = (c) => api.get(`/smart-fulfilment/legacy-feedback/preview/${c}`).then((r) => setHtml(r.data.html));
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/smart-fulfilment/legacy-feedback/send", { codes: picked, date_from: FROM, date_to: TO, confirm });
      setResults(r.data.results);
      setPicked([]);
      setConfirm("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  if (!audit) return <div className="p-6 text-sm text-slate-500" data-testid="lfb-loading">Loading…</div>;
  const need = `SEND ${picked.length}`;
  return (
    <div className="mx-auto max-w-6xl space-y-4 p-6" data-testid="legacy-feedback-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Legacy delivery feedback catch-up</h1>
        <p className="text-[12px] text-slate-600">Older SHP- shipments dispatched {FROM} – {TO} (IST). Read-only until you send. Only ticked ELIGIBLE rows are sent, each once; no reminders.</p>
      </div>
      <div className={card} data-testid="lfb-totals">{Object.entries(audit.totals).map(([k, v]) => <span key={k} className="mr-3 text-[12px]"><b>{k}</b> {v}</span>)}</div>
      <div className={`${card} overflow-x-auto`}>
        <table className="min-w-full" data-testid="lfb-table">
          <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500"><th /><th className="p-2">Shipment</th><th className="p-2">Recipient</th><th className="p-2">Email</th><th className="p-2">Dispatched</th><th className="p-2">Status</th><th /></tr></thead>
          <tbody>{audit.rows.map((r) => <Row key={r.id} r={r} picked={picked.includes(r.code)} toggle={toggle} preview={preview} />)}</tbody>
        </table>
      </div>
      <div className={`${card} flex flex-wrap items-center gap-2`}>
        <span className="text-[12px]">Selected: <b data-testid="lfb-selected">{picked.join(", ") || "none"}</b></span>
        <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={`Type ${need}`} className="rounded-md border border-slate-300 px-2 py-1 text-[12px]" data-testid="lfb-confirm" />
        <button onClick={send} disabled={busy || !picked.length || confirm !== need} className="rounded-full bg-slate-900 px-4 py-1.5 text-[12px] font-bold text-white disabled:bg-slate-300" data-testid="lfb-send">Send feedback requests</button>
      </div>
      {results && <div className={card} data-testid="lfb-results">{results.map((x) => <div key={x.code} className="text-[12px]"><b>{x.code}</b> · {x.result}{x.class ? ` · ${x.class}` : ""}{x.reason ? ` · ${x.reason}` : ""}{x.error ? ` · ${x.error}` : ""}</div>)}</div>}
      <JobPanel />
      <CustomerNoticePanel />
      {html && <iframe title="Email preview" srcDoc={html} className="h-[720px] w-full rounded-xl border border-slate-200 bg-white" data-testid="lfb-preview-frame" />}
    </div>
  );
}
